import {
  type ChannelsView,
  channelsViewSchema,
  type SalesMapView,
  type SiteChatList,
  salesMapViewSchema,
  siteChatListSchema,
  type VoiceJobCreated,
  type VoiceView,
  voiceJobCreatedSchema,
  voiceViewSchema,
} from '@twin/contracts';
/**
 * The real TwinApi over HTTP (specs/c2-kontratlar.md 2). Same origin: the panel's Caddy
 * proxies /api/* to the API and adds the edge secret; the Tailscale session identifies
 * Sercan. Every request and response is checked against the @twin/contracts schemas, so a
 * field name mismatch fails loudly at the boundary instead of silently in a screen.
 *
 * Rules carried here:
 * - every POST and PUT sends X-Twin-Request: 1 (CSRF, 2.2);
 * - approve (approve and edit_approve) and hold send X-Twin-View, the last SSE event id the
 *   panel processed (K15); without one the server answers 409 stale_view;
 * - error bodies become TwinApiError with the contract code (2.7); 409 already_applied on
 *   undo becomes AlreadyAppliedError;
 * - ids on the wire are decimal strings, instants in the snapshot epoch ms, resource times
 *   ISO 8601 (K10); the ticket of a decision is built with approvalTicketFrom.
 */

import {
  API_BASE_PATH,
  agentControlResponseSchema,
  apiErrorSchema,
  apiUsageViewSchema,
  approvalTicketFrom,
  CONNECTION_SLOT_PANEL_ID,
  CONNECTION_SLOTS,
  type ConnectionSlot,
  configAgentIdOf,
  connectionClearResponseSchema,
  connectionSetResponseSchema,
  connectionsResponseSchema,
  connectionTestResponseSchema,
  customersViewSchema,
  decisionResponseSchema,
  evalViewSchema,
  fitLabelResponseSchema,
  HEADERS,
  holdResponseSchema,
  kbDecisionResponseSchema,
  kbViewSchema,
  killSwitchResponseSchema,
  learningBatchResultSchema,
  learningViewSchema,
  manualDoneResponseSchema,
  mcpAddressCreatedSchema,
  mcpAddressListSchema,
  mcpAddressRevokedSchema,
  type OzetSummaryView,
  type OzetView,
  organizationViewSchema,
  outreachCampaignApproveResponseSchema,
  outreachLogSchema,
  outreachLogTextSchema,
  outreachMessageDecisionResponseSchema,
  outreachRefutationResponseSchema,
  outreachViewSchema,
  ozetSummaryViewSchema,
  ozetViewSchema,
  personActivitySchema,
  productsViewSchema,
  replyExampleDecisionResponseSchema,
  replyExamplesViewSchema,
  requestDecisionResponseSchema,
  revertResponseSchema,
  revertResultSchema,
  type SeoReportView,
  type SiteStatsView,
  sekreterDigestSchema,
  sekreterItemResponseSchema,
  sendReplyResponseSchema,
  seoReportViewSchema,
  siteStatsViewSchema,
  snapshotSchema,
  switchResponseSchema,
  undoResponseSchema,
} from '@twin/contracts';
import type { ApiUsageView } from './api-usage.js';
import { AlreadyAppliedError, PolicyRejectedError, TransportError, type TwinApi, TwinApiError } from './client.js';
import type { CustomersView } from './customers.js';
import type { Basemap, CellDetail, LeadDetail, LeadsView } from './leads.js';
import type { LearningView } from './learning.js';
import type { LogView } from './log.js';
import type { OrganizationView } from './organization.js';
import type { PersonActivity } from './people.js';
import type { ProductsView } from './products.js';
import type { ProspectDetail, ProspectsView } from './prospects.js';
import type { AuditEntry, PolicyChange, PolicyChangeInput, Settings } from './settings.js';
import type { ShopView } from './shop.js';
import type { AgentId, ApprovalTicket, HoldTicket } from './types.js';

/** The part of a zod schema the client uses; keeps this file free of a direct zod import. */
interface Schema<T> {
  safeParse(value: unknown): { success: true; data: T } | { success: false; error: { issues: readonly unknown[] } };
}

export type FetchLike = (input: string, init: RequestInit) => Promise<Response>;

export interface HttpApiOptions {
  /** API base path, "/api/v1" by default. */
  basePath?: string;
  fetch?: FetchLike;
  /** Last processed SSE event id for X-Twin-View (from LiveEvents.lastEventId). */
  viewId: () => string | null;
  /** Local monotonic clock for the hold ticket (performance.now by default). */
  localNow?: () => number;
  /** Server time as epoch ms, for audit entries built from a response (Date.now by default). */
  now?: () => number;
  /** Random part of Idempotency-Key values (crypto.randomUUID by default). */
  uuid?: () => string;
  /** Request timeout in milliseconds. */
  timeoutMs?: number;
}

const DEFAULT_TIMEOUT_MS = 10_000;

/** Shown by Leadler in live mode for the learning decisions the API does not take yet (revert, batch, fit label). */
export const LEARNING_NOT_SERVED = 'Öğrenme kararları sunucuda henüz yok; karar uç noktaları gelince burada görünür';

function defaultUuid(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return [...bytes].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

function segment(value: string): string {
  return encodeURIComponent(value);
}

interface Call {
  method: 'GET' | 'POST' | 'PUT' | 'DELETE';
  path: string;
  body?: unknown;
  view?: boolean;
  idempotencyKey?: string;
}

export function createHttpApi(options: HttpApiOptions): TwinApi {
  const basePath = options.basePath ?? API_BASE_PATH;
  const doFetch: FetchLike = options.fetch ?? ((input, init) => fetch(input, init));
  const localNow = options.localNow ?? (() => performance.now());
  const now = options.now ?? (() => Date.now());
  const uuid = options.uuid ?? defaultUuid;
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;

  async function send(call: Call): Promise<unknown> {
    const headers: Record<string, string> = { accept: 'application/json' };
    if (call.method !== 'GET') headers[HEADERS.twinRequest] = '1';
    if (call.body !== undefined) headers['content-type'] = 'application/json; charset=utf-8';
    if (call.view) {
      const view = options.viewId();
      if (view !== null) headers[HEADERS.twinView] = view;
    }
    if (call.idempotencyKey) headers[HEADERS.idempotencyKey] = call.idempotencyKey;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    let response: Response;
    try {
      const init: RequestInit = {
        method: call.method,
        headers,
        credentials: 'same-origin',
        cache: 'no-store',
        signal: controller.signal,
      };
      if (call.body !== undefined) init.body = JSON.stringify(call.body);
      response = await doFetch(`${basePath}${call.path}`, init);
    } catch (error) {
      throw new TransportError(error instanceof Error ? error.message : 'istek gitmedi');
    } finally {
      clearTimeout(timer);
    }
    const text = await response.text();
    let json: unknown = null;
    if (text !== '') {
      try {
        json = JSON.parse(text);
      } catch {
        json = null;
      }
    }
    if (!response.ok) {
      const parsed = apiErrorSchema.safeParse(json);
      if (parsed.success) {
        const { code, message } = parsed.data.error;
        throw new TwinApiError(code, message, response.status, parsed.data.requestId);
      }
      throw new TwinApiError(
        response.status >= 500 ? 'internal' : 'unknown',
        `Sunucu hatası (${response.status})`,
        response.status,
        response.headers.get(HEADERS.requestId),
      );
    }
    return json;
  }

  /** Structural check of GET /leads until @twin/contracts carries its schema. */
  const logSchema: Schema<LogView> = {
    safeParse(value: unknown) {
      const v = value as Partial<LogView> | null;
      const ok = v !== null && typeof v === 'object' && typeof v.asOf === 'number' && Array.isArray(v.events);
      return ok
        ? { success: true as const, data: v as LogView }
        : { success: false as const, error: { issues: [{ message: 'log shape' }] } };
    },
  };
  const shopSyncResponseSchema: Schema<{ queued: boolean }> = {
    safeParse(value: unknown) {
      const v = value as { queued?: unknown } | null;
      return v !== null && typeof v === 'object' && typeof v.queued === 'boolean'
        ? { success: true as const, data: { queued: v.queued } }
        : { success: false as const, error: { issues: [{ message: 'shop sync shape' }] } };
    },
  };
  const shopSchema: Schema<ShopView> = {
    safeParse(value: unknown) {
      const v = value as Partial<ShopView> | null;
      const ok =
        v !== null &&
        typeof v === 'object' &&
        typeof v.asOf === 'number' &&
        Array.isArray(v.listings) &&
        Array.isArray(v.orders) &&
        Array.isArray(v.countries) &&
        Array.isArray(v.monthly) &&
        typeof v.listingCounts === 'object' &&
        v.listingCounts !== null;
      return ok
        ? { success: true as const, data: v as ShopView }
        : { success: false as const, error: { issues: [{ message: 'shop shape' }] } };
    },
  };
  const leadsSchema: Schema<LeadsView> = {
    safeParse(value: unknown) {
      const v = value as Partial<LeadsView> | null;
      const ok =
        v !== null &&
        typeof v === 'object' &&
        typeof v.asOf === 'number' &&
        typeof v.world === 'object' &&
        v.world !== null &&
        Array.isArray(v.world.regions) &&
        Array.isArray(v.regions) &&
        Array.isArray(v.cells) &&
        Array.isArray(v.funnel) &&
        Array.isArray(v.companies);
      return ok
        ? { success: true, data: value as LeadsView }
        : { success: false, error: { issues: [{ message: 'leads shape' }] } };
    },
  };

  const prospectsSchema: Schema<ProspectsView> = {
    safeParse(value: unknown) {
      const v = value as Partial<ProspectsView> | null;
      const ok =
        v !== null &&
        typeof v === 'object' &&
        typeof v.asOf === 'number' &&
        typeof v.summary === 'object' &&
        v.summary !== null &&
        typeof v.costs === 'object' &&
        v.costs !== null &&
        typeof v.jobs === 'object' &&
        v.jobs !== null &&
        Array.isArray(v.jobs.running) &&
        Array.isArray(v.jobs.next) &&
        Array.isArray(v.jobs.recent) &&
        Array.isArray(v.rows);
      return ok
        ? { success: true, data: value as ProspectsView }
        : { success: false, error: { issues: [{ message: 'prospects shape' }] } };
    },
  };

  const prospectSchema: Schema<ProspectDetail> = {
    safeParse(value: unknown) {
      const v = value as Partial<ProspectDetail> | null;
      const ok =
        v !== null &&
        typeof v === 'object' &&
        typeof v.row === 'object' &&
        v.row !== null &&
        typeof v.row.leadId === 'string' &&
        Array.isArray(v.found) &&
        Array.isArray(v.evidence) &&
        Array.isArray(v.decisions) &&
        Array.isArray(v.history) &&
        Array.isArray(v.channels) &&
        Array.isArray(v.messages);
      return ok
        ? { success: true, data: value as ProspectDetail }
        : { success: false, error: { issues: [{ message: 'prospect shape' }] } };
    },
  };

  const basemapSchema: Schema<Basemap> = {
    safeParse(value: unknown) {
      const v = value as Partial<Basemap> | null;
      const ok = v !== null && typeof v === 'object' && typeof v.region === 'string' && Array.isArray(v.layers);
      return ok
        ? { success: true, data: value as Basemap }
        : { success: false, error: { issues: [{ message: 'basemap shape' }] } };
    },
  };

  const cellDetailSchema: Schema<CellDetail> = {
    safeParse(value: unknown) {
      const v = value as Partial<CellDetail> | null;
      const ok =
        v !== null &&
        typeof v === 'object' &&
        typeof v.asOf === 'number' &&
        typeof v.cell === 'object' &&
        v.cell !== null &&
        typeof v.cell.cellId === 'string' &&
        Array.isArray(v.runs) &&
        Array.isArray(v.queries) &&
        Array.isArray(v.companies);
      return ok
        ? { success: true, data: value as CellDetail }
        : { success: false, error: { issues: [{ message: 'cell shape' }] } };
    },
  };

  /** GET /learning is checked with the contract schema (v0.1.27); the panel's own type has the same shape. */
  const learningSchema: Schema<LearningView> = {
    safeParse(value: unknown) {
      const parsed = learningViewSchema.safeParse(value);
      return parsed.success
        ? { success: true, data: parsed.data as unknown as LearningView }
        : { success: false, error: { issues: parsed.error.issues } };
    },
  };

  /** GET /customers is checked with the contract schema (v0.1.58). */
  const customersSchema: Schema<CustomersView> = {
    safeParse(value: unknown) {
      const parsed = customersViewSchema.safeParse(value);
      return parsed.success
        ? { success: true, data: parsed.data }
        : { success: false, error: { issues: parsed.error.issues } };
    },
  };

  /** GET /settings/api-usage is checked with the contract schema (v0.1.164). */
  const apiUsageSchema: Schema<ApiUsageView> = {
    safeParse(value: unknown) {
      const parsed = apiUsageViewSchema.safeParse(value);
      return parsed.success
        ? { success: true, data: parsed.data }
        : { success: false, error: { issues: parsed.error.issues } };
    },
  };

  /** GET /site/stats is checked with the contract schema (v0.1.166). */
  const siteStatsSchema: Schema<SiteStatsView> = {
    safeParse(value: unknown) {
      const parsed = siteStatsViewSchema.safeParse(value);
      return parsed.success
        ? { success: true, data: parsed.data }
        : { success: false, error: { issues: parsed.error.issues } };
    },
  };

  /** GET /site-chats is checked with the contract schema (v0.1.248). */
  const siteChatsSchema: Schema<SiteChatList> = {
    safeParse(value: unknown) {
      const parsed = siteChatListSchema.safeParse(value);
      return parsed.success
        ? { success: true, data: parsed.data }
        : { success: false, error: { issues: parsed.error.issues } };
    },
  };
  /** GET /channels is checked with the contract schema (28 Oct 2026). */
  const channelsSchema: Schema<ChannelsView> = {
    safeParse(value: unknown) {
      const parsed = channelsViewSchema.safeParse(value);
      return parsed.success
        ? { success: true, data: parsed.data }
        : { success: false, error: { issues: parsed.error.issues } };
    },
  };

  /** GET /voice and POST /voice/jobs are checked with the contract schemas (v0.1.255). */
  const voiceSchema: Schema<VoiceView> = {
    safeParse(value: unknown) {
      const parsed = voiceViewSchema.safeParse(value);
      return parsed.success
        ? { success: true, data: parsed.data }
        : { success: false, error: { issues: parsed.error.issues } };
    },
  };
  const voiceCreatedSchema: Schema<VoiceJobCreated> = {
    safeParse(value: unknown) {
      const parsed = voiceJobCreatedSchema.safeParse(value);
      return parsed.success
        ? { success: true, data: parsed.data }
        : { success: false, error: { issues: parsed.error.issues } };
    },
  };

  /** GET /site/seo is checked with the contract schema (v0.1.190). */
  const seoReportSchema: Schema<SeoReportView> = {
    safeParse(value: unknown) {
      const parsed = seoReportViewSchema.safeParse(value);
      return parsed.success
        ? { success: true, data: parsed.data }
        : { success: false, error: { issues: parsed.error.issues } };
    },
  };

  /** GET /ozet is checked with the contract schema (v0.1.175). */
  const ozetSchema: Schema<OzetView> = {
    safeParse(value: unknown) {
      const parsed = ozetViewSchema.safeParse(value);
      return parsed.success
        ? { success: true, data: parsed.data }
        : { success: false, error: { issues: parsed.error.issues } };
    },
  };

  /** GET /sales-map is checked with the contract schema (v0.1.269). */
  const salesMapSchema: Schema<SalesMapView> = {
    safeParse(value: unknown) {
      const parsed = salesMapViewSchema.safeParse(value);
      return parsed.success
        ? { success: true, data: parsed.data }
        : { success: false, error: { issues: parsed.error.issues } };
    },
  };

  /** GET /ozet/summary is checked with the contract schema (v0.1.257). */
  const ozetSummarySchema: Schema<OzetSummaryView> = {
    safeParse(value: unknown) {
      const parsed = ozetSummaryViewSchema.safeParse(value);
      return parsed.success
        ? { success: true, data: parsed.data }
        : { success: false, error: { issues: parsed.error.issues } };
    },
  };

  /** GET /team/organization is checked with the contract schema (v0.1.150). */
  const organizationSchema: Schema<OrganizationView> = {
    safeParse(value: unknown) {
      const parsed = organizationViewSchema.safeParse(value);
      return parsed.success
        ? { success: true, data: parsed.data }
        : { success: false, error: { issues: parsed.error.issues } };
    },
  };

  /** GET /people/{id}/activity is checked with the contract schema (v0.1.64). */
  const personSchema: Schema<PersonActivity> = {
    safeParse(value: unknown) {
      const parsed = personActivitySchema.safeParse(value);
      return parsed.success
        ? { success: true, data: parsed.data }
        : { success: false, error: { issues: parsed.error.issues } };
    },
  };

  /** GET /shop/products is checked with the contract schema (v0.1.70). */
  const productsSchema: Schema<ProductsView> = {
    safeParse(value: unknown) {
      const parsed = productsViewSchema.safeParse(value);
      return parsed.success
        ? { success: true, data: parsed.data }
        : { success: false, error: { issues: parsed.error.issues } };
    },
  };

  /** Structural check of GET /leads/{leadId}. */
  const leadDetailSchema: Schema<LeadDetail> = {
    safeParse(value: unknown) {
      const v = value as Partial<LeadDetail> | null;
      const ok =
        v !== null &&
        typeof v === 'object' &&
        typeof v.leadId === 'string' &&
        typeof v.name === 'string' &&
        v.fit !== undefined &&
        typeof v.fit.total === 'number' &&
        Array.isArray(v.evidence) &&
        Array.isArray(v.history);
      return ok
        ? { success: true, data: value as LeadDetail }
        : { success: false, error: { issues: [{ message: 'lead detail shape' }] } };
    },
  };

  /** Structural check of GET /settings until @twin/contracts carries its schema. */
  const settingsSchema: Schema<Settings> = {
    safeParse(value: unknown) {
      const v = value as Partial<Settings> | null;
      const ok =
        v !== null &&
        typeof v === 'object' &&
        typeof v.now === 'number' &&
        Array.isArray(v.connections) &&
        Array.isArray(v.groups) &&
        Array.isArray(v.policies) &&
        Array.isArray(v.flags) &&
        Array.isArray(v.hardLimits) &&
        Array.isArray(v.agents) &&
        Array.isArray(v.audit) &&
        v.stage !== undefined &&
        v.preferences !== undefined &&
        v.schedule !== undefined &&
        v.backup !== undefined &&
        v.security !== undefined &&
        v.about !== undefined &&
        v.repo !== undefined;
      return ok
        ? { success: true, data: value as Settings }
        : { success: false, error: { issues: [{ message: 'settings shape' }] } };
    },
  };

  async function request<T>(call: Call, schema: Schema<T>): Promise<T> {
    const json = await send(call);
    const parsed = schema.safeParse(json);
    if (!parsed.success) throw new TransportError(`${call.method} ${call.path}: yanıt kontrata uymuyor`);
    return parsed.data;
  }

  const panel = { channel: 'panel' as const };

  const setKillSwitch = (level: 'none' | 'pause' | 'stop', holdToken: string | null = null) =>
    request(
      {
        method: 'PUT',
        path: '/system/kill-switch',
        body: holdToken === null ? { level, reason: null, ...panel } : { level, reason: null, holdToken, ...panel },
      },
      killSwitchResponseSchema,
    );

  const setAgent = async (id: AgentId, action: 'pause' | 'resume', reason?: string) =>
    controlConfigAgent(configAgentIdOf(id), action, reason);

  /** Pause or resume an agent by its config id (growth agents have no panel id). */
  const controlConfigAgent = async (configId: string, action: 'pause' | 'resume', reason?: string) => {
    const body = reason === undefined ? panel : { ...panel, reason };
    const path = `/agents/${segment(configId)}/${action}`;
    try {
      await request({ method: 'POST', path, body }, agentControlResponseSchema);
    } catch (error) {
      // v0.1.83: a growth agent resumes only with the completed hold (the server says hold_required
      // first); the hold is taken here like a switch's and the resume is sent again with its token.
      if (!(error instanceof TwinApiError && error.code === 'hold_required' && action === 'resume')) throw error;
      const ticket = await startHoldAt(`/agents/${segment(configId)}/hold`, {});
      await waitUntilLocal(ticket.sendNotBeforeLocal);
      await request(
        { method: 'POST', path, body: { ...body, holdToken: ticket.holdToken } },
        agentControlResponseSchema,
      );
    }
  };

  /**
   * A switch change (only executor.dry_run is a panel switch in C2). Loosening needs a hold
   * token for "switch:<key>": the token is asked when the hold has completed, and the change
   * is sent once the server's notBefore has passed.
   */
  const setSwitch = async (key: string, enabled: boolean, loosening: boolean, reason: string) => {
    let holdToken: string | undefined;
    if (loosening) {
      const ticket = await startHoldAt(`/system/switches/${segment(key)}/hold`, { enabled });
      await waitUntilLocal(ticket.sendNotBeforeLocal);
      holdToken = ticket.holdToken;
    }
    const body = holdToken === undefined ? { enabled, reason, ...panel } : { enabled, reason, holdToken, ...panel };
    return request({ method: 'PUT', path: `/system/switches/${segment(key)}`, body }, switchResponseSchema);
  };

  const waitUntilLocal = (at: number) =>
    new Promise<void>((resolve) => {
      const delay = at - localNow();
      if (delay <= 0) resolve();
      else setTimeout(resolve, Math.ceil(delay));
    });

  async function startHoldAt(path: string, body: unknown): Promise<HoldTicket> {
    const response = await request({ method: 'POST', path, body, view: true }, holdResponseSchema);
    const receivedAt = localNow();
    const serverNow = Date.parse(response.serverNow);
    const notBefore = Date.parse(response.notBefore);
    return {
      holdToken: response.holdToken,
      // The token is valid from notBefore in server time; translated to the local clock
      // from the moment the answer arrived, so a slow answer never yields hold_too_short.
      sendNotBeforeLocal: receivedAt + Math.max(0, notBefore - serverNow),
      expiresAt: Date.parse(response.expiresAt),
    };
  }

  const audit = (change: PolicyChange, text: string, direction: AuditEntry['direction'], reason: string | null) => ({
    id: `api-${change.id}-${now()}`,
    at: now(),
    actor: 'Sercan' as const,
    target: change.id,
    targetLabel: change.id,
    change: text,
    direction,
    reason,
  });

  /** Policy screen flags and the system switch each one drives (executor_dry_run is handled above). */
  const FLAG_SWITCH: Readonly<Record<string, string>> = {
    lead_scan: 'feature.lead_engine',
    lead_verify: 'feature.lead_verifier',
    outreach: 'outreach.sending',
    social_posting: 'platform.instagram',
  };
  /** v0.1.88: the growth agent row a lead flag opens together with its switch. */
  const FLAG_AGENT: Readonly<Record<string, string>> = {
    lead_scan: 'bolge-temsilcisi',
    lead_verify: 'dogrulayici',
  };

  async function changePolicy(input: PolicyChangeInput): Promise<AuditEntry> {
    const { change } = input;
    const reason = input.reason ?? 'panel: politika değişikliği';
    if (change.kind === 'flag' && change.id === 'executor_dry_run') {
      // Turning the dry run off loosens; it needs the completed hold in the panel and a token here.
      const loosening = !change.enabled;
      if (loosening && !input.confirmedByHold) {
        throw new PolicyRejectedError('hold_required', 'Gevşetmek için basılı tut');
      }
      const result = await setSwitch('executor.dry_run', change.enabled, loosening, reason);
      return audit(
        change,
        result.enabled ? 'Kapalı → Açık' : 'Açık → Kapalı',
        loosening ? 'loosen' : 'tighten',
        input.reason,
      );
    }
    if (change.kind === 'flag') {
      // v0.1.79: the feature switches of the policy screen. Off is the safe side of each one, so
      // turning one on loosens and needs the completed hold; the server checks its own locks
      // (outreach.sending stays refused in C2).
      const key = FLAG_SWITCH[change.id];
      if (key === undefined) throw new PolicyRejectedError('unknown', 'Bu ayar sunucuda henüz değiştirilemez');
      const loosening = change.enabled;
      if (loosening && !input.confirmedByHold) {
        throw new PolicyRejectedError('hold_required', 'Gevşetmek için basılı tut');
      }
      const result = await setSwitch(key, change.enabled, loosening, reason);
      const agent = FLAG_AGENT[change.id];
      if (loosening && agent !== undefined) await controlConfigAgent(agent, 'resume', reason);
      return audit(
        change,
        result.enabled ? 'Kapalı → Açık' : 'Açık → Kapalı',
        loosening ? 'loosen' : 'tighten',
        input.reason,
      );
    }
    if (change.kind === 'agent') {
      await setAgent(change.id, change.enabled ? 'resume' : 'pause', input.reason ?? undefined);
      return audit(
        change,
        change.enabled ? 'Kapalı → Açık' : 'Açık → Kapalı',
        change.enabled ? 'loosen' : 'tighten',
        input.reason,
      );
    }
    throw new PolicyRejectedError('unknown', 'Bu ayar sunucuda henüz değiştirilemez');
  }

  return {
    getSnapshot: () => request({ method: 'GET', path: '/panel/snapshot' }, snapshotSchema),

    startHold: (proposalId, payloadHash) => startHoldAt(`/approvals/${segment(proposalId)}/hold`, { payloadHash }),

    async approve(proposalId, input): Promise<ApprovalTicket> {
      const body =
        input.editedText !== null
          ? { decision: 'edit_approve', payloadHash: input.payloadHash, editedText: input.editedText, ...panel }
          : {
              decision: 'approve',
              payloadHash: input.payloadHash,
              ...panel,
              ...(input.holdToken !== null ? { holdToken: input.holdToken } : {}),
              ...(input.copyConfirmed ? { copyConfirmed: true } : {}),
            };
      const response = await request(
        { method: 'POST', path: `/approvals/${segment(proposalId)}/decision`, body, view: true },
        decisionResponseSchema,
      );
      return approvalTicketFrom(response);
    },

    async undo(ticketId) {
      try {
        const response = await request(
          { method: 'POST', path: `/approvals/${segment(ticketId)}/undo`, body: panel },
          undoResponseSchema,
        );
        return { requeuedProposalId: response.requeuedApprovalId };
      } catch (error) {
        if (error instanceof TwinApiError && error.code === 'already_applied') {
          throw new AlreadyAppliedError(ticketId, error.requestId);
        }
        throw error;
      }
    },

    async reject(proposalId, reason, note, payloadHash) {
      const trimmed = note.trim();
      const body = {
        decision: 'reject',
        payloadHash,
        rejectReasonCode: reason,
        ...panel,
        ...(trimmed === '' ? {} : { note: trimmed }),
      };
      await request(
        { method: 'POST', path: `/approvals/${segment(proposalId)}/decision`, body },
        decisionResponseSchema,
      );
    },

    async markSent(proposalId) {
      await request(
        { method: 'POST', path: `/approvals/${segment(proposalId)}/manual-done`, body: panel },
        manualDoneResponseSchema,
      );
    },

    async sendReply(approvalId) {
      await request(
        { method: 'POST', path: `/approvals/${segment(approvalId)}/send`, body: panel },
        sendReplyResponseSchema,
      );
    },

    async revert(doneId) {
      const response = await request(
        { method: 'POST', path: `/done/${segment(doneId)}/revert`, idempotencyKey: `panel:revert:${doneId}:${uuid()}` },
        revertResponseSchema,
      );
      return approvalTicketFrom(response);
    },

    async getKb() {
      return request({ method: 'GET', path: '/kb' }, kbViewSchema);
    },

    async approveKb(id, input) {
      await request({ method: 'POST', path: `/kb/${segment(id)}/approve`, body: input }, kbDecisionResponseSchema);
    },

    async retireKb(id) {
      await request({ method: 'POST', path: `/kb/${segment(id)}/retire`, body: {} }, kbDecisionResponseSchema);
    },
    getSekreter: () => request({ method: 'GET', path: '/sekreter' }, sekreterDigestSchema),
    async addSekreterItem(input) {
      await request({ method: 'POST', path: '/sekreter/items', body: input }, sekreterItemResponseSchema);
    },
    async doneSekreterItem(id) {
      await request(
        { method: 'POST', path: `/sekreter/items/${segment(id)}/done`, body: {} },
        sekreterItemResponseSchema,
      );
    },
    async reopenSekreterItem(id) {
      await request(
        { method: 'POST', path: `/sekreter/items/${segment(id)}/reopen`, body: {} },
        sekreterItemResponseSchema,
      );
    },
    getReplyExamples: () => request({ method: 'GET', path: '/reply-examples' }, replyExamplesViewSchema),
    getEvals: () => request({ method: 'GET', path: '/evals/temsilci' }, evalViewSchema),
    async retireReplyExample(id) {
      await request(
        { method: 'POST', path: `/reply-examples/${segment(id)}/retire`, body: {} },
        replyExampleDecisionResponseSchema,
      );
    },
    async restoreReplyExample(id) {
      await request(
        { method: 'POST', path: `/reply-examples/${segment(id)}/restore`, body: {} },
        replyExampleDecisionResponseSchema,
      );
    },

    async answerRequest(id, input) {
      await request(
        { method: 'POST', path: `/requests/${segment(id)}/answer`, body: { text: input.text, addToKb: input.addToKb } },
        requestDecisionResponseSchema,
      );
    },

    async acceptRequest(id, input) {
      await request(
        {
          method: 'POST',
          path: `/requests/${segment(id)}/accept`,
          body: { targetDate: input.targetDate, note: input.note },
        },
        requestDecisionResponseSchema,
      );
    },

    async deferRequest(id, until) {
      await request(
        { method: 'POST', path: `/requests/${segment(id)}/postpone`, body: { until } },
        requestDecisionResponseSchema,
      );
    },

    async rejectRequest(id, reason, note) {
      await request(
        { method: 'POST', path: `/requests/${segment(id)}/reject`, body: { reasonCode: reason, note } },
        requestDecisionResponseSchema,
      );
    },

    pauseAgent: (id) => setAgent(id, 'pause'),
    resumeAgent: (id) => setAgent(id, 'resume'),

    async pauseTeam() {
      await setKillSwitch('pause');
    },

    async emergencyStop() {
      const response = await setKillSwitch('stop');
      return {
        stoppedAt: Date.parse(response.updatedAt),
        requeued: response.requeued.map((item) => ({ from: item.fromApprovalId, to: item.toApprovalId })),
      };
    },

    startResumeHold: () => startHoldAt('/system/kill-switch/hold', undefined),

    async resumeTeam(holdToken) {
      const response = await setKillSwitch('none', holdToken);
      return { pendingExecutions: response.pendingExecutions };
    },

    // ---- Ayarlar ----
    // GET /settings (EXTRA_ROUTES on the api) already answers the panel's Settings shape;
    // it has no zod schema in @twin/contracts yet, so the response is checked structurally
    // here (0.4.1 follow-up: move the schema into contracts).
    getSettings: () => request({ method: 'GET', path: '/settings' }, settingsSchema),
    getApiUsage: () => request({ method: 'GET', path: '/settings/api-usage' }, apiUsageSchema),
    getSiteStats: () => request({ method: 'GET', path: '/site/stats' }, siteStatsSchema),
    getChannels: () => request({ method: 'GET', path: '/channels' }, channelsSchema),
    getSalesMap: (period, category) =>
      request(
        {
          method: 'GET',
          path: `/sales-map?period=${encodeURIComponent(period)}&category=${encodeURIComponent(category)}`,
        },
        salesMapSchema,
      ),
    getVoice: () => request({ method: 'GET', path: '/voice' }, voiceSchema),
    createVoiceJob: (body) => request({ method: 'POST', path: '/voice/jobs', body }, voiceCreatedSchema),
    getSiteChats: (before) =>
      request(
        {
          method: 'GET',
          path: before === undefined ? '/site-chats' : `/site-chats?before=${encodeURIComponent(before)}`,
        },
        siteChatsSchema,
      ),
    getSeoReport: () => request({ method: 'GET', path: '/site/seo' }, seoReportSchema),
    getOzet: () => request({ method: 'GET', path: '/ozet' }, ozetSchema),
    getOzetSummary: (period) =>
      request({ method: 'GET', path: `/ozet/summary?period=${encodeURIComponent(period)}` }, ozetSummarySchema),
    listMcpAddresses: () => request({ method: 'GET', path: '/settings/mcp' }, mcpAddressListSchema),
    createMcpAddress: (label) =>
      request({ method: 'POST', path: '/settings/mcp', body: { label } }, mcpAddressCreatedSchema),
    revokeMcpAddress: (id) =>
      request({ method: 'POST', path: `/settings/mcp/${segment(id)}/revoke`, body: {} }, mcpAddressRevokedSchema),
    changePolicy,
    /**
     * Tüm yetkileri iptal et: for the connections whose grant is a value entered in the panel
     * (Telegram, Claude, Genesis) this clears every slot of the connection on the server; the
     * others have no server side revoke yet.
     */
    async revokeConnection(id): Promise<AuditEntry> {
      const slots = CONNECTION_SLOTS.filter((slot) => CONNECTION_SLOT_PANEL_ID[slot] === id);
      if (slots.length === 0) throw new PolicyRejectedError('unknown', 'Yetki iptali sunucuda henüz yok');
      for (const slot of slots) {
        await request({ method: 'DELETE', path: `/connections/${segment(slot)}` }, connectionClearResponseSchema);
      }
      return {
        id: `api-revoke-${id}-${now()}`,
        at: now(),
        actor: 'Sercan',
        target: id,
        targetLabel: id,
        change: 'kayıtlı değerler silindi',
        direction: 'tighten',
        reason: null,
      };
    },
    async updatePreferences() {
      throw new PolicyRejectedError('unknown', 'Tercihler sunucuda henüz saklanmıyor');
    },

    // ---- Bağlantı değerleri (0.4.1) ----
    getConnections: () => request({ method: 'GET', path: '/connections' }, connectionsResponseSchema),
    setConnection: (slot: ConnectionSlot, value: string) =>
      request({ method: 'PUT', path: `/connections/${segment(slot)}`, body: { value } }, connectionSetResponseSchema),
    clearConnection: (slot: ConnectionSlot) =>
      request({ method: 'DELETE', path: `/connections/${segment(slot)}` }, connectionClearResponseSchema),
    testConnection: (slot: ConnectionSlot) =>
      request({ method: 'POST', path: `/connections/${segment(slot)}/test` }, connectionTestResponseSchema),

    // ---- Leadler ----
    // GET /leads (EXTRA_ROUTES on the api) answers the map and the funnel; it has no
    // zod schema in @twin/contracts yet, so the shape is checked structurally here.
    // GET /learning (v0.1.27) is the live model; fit labels (v0.1.33), reverts of automatic
    // changes (v0.1.34) and the weekly batch decision (v0.1.35) are served.
    getLeads: () => request({ method: 'GET', path: '/leads' }, leadsSchema),
    getLeadDetail: (leadId) => request({ method: 'GET', path: `/leads/${segment(leadId)}` }, leadDetailSchema),
    getCellDetail: (cellId) => request({ method: 'GET', path: `/leads/cells/${segment(cellId)}` }, cellDetailSchema),
    getBasemap: (region) => request({ method: 'GET', path: `/leads/basemap/${segment(region)}` }, basemapSchema),
    getProspects: () => request({ method: 'GET', path: '/leads/prospects' }, prospectsSchema),
    getProspect: (leadId) => request({ method: 'GET', path: `/leads/prospects/${segment(leadId)}` }, prospectSchema),
    getOutreach: () => request({ method: 'GET', path: '/leads/outreach' }, outreachViewSchema),
    getOutreachLog: () => request({ method: 'GET', path: '/leads/outreach/log' }, outreachLogSchema),
    getOutreachLogText: (messageId) =>
      request({ method: 'GET', path: `/leads/outreach/log/${segment(messageId)}` }, outreachLogTextSchema),
    approveOutreachCampaign: async (code) => {
      const ticket = await startHoldAt(`/leads/outreach/campaigns/${segment(code)}/hold`, {});
      await waitUntilLocal(ticket.sendNotBeforeLocal);
      return request(
        {
          method: 'POST',
          path: `/leads/outreach/campaigns/${segment(code)}/approve`,
          body: { holdToken: ticket.holdToken },
        },
        outreachCampaignApproveResponseSchema,
      );
    },
    decideOutreachMessage: (messageId, input) =>
      request(
        { method: 'POST', path: `/leads/outreach/messages/${segment(messageId)}/decision`, body: input },
        outreachMessageDecisionResponseSchema,
      ),
    refuteOutreachAudit: (auditId, itemNo, decision) =>
      request(
        {
          method: 'POST',
          path: `/leads/outreach/audits/${segment(auditId)}/items/${segment(String(itemNo))}`,
          body: { decision },
        },
        outreachRefutationResponseSchema,
      ),
    getShop: () => request({ method: 'GET', path: '/shop' }, shopSchema),
    requestShopSync: () => request({ method: 'POST', path: '/shop/sync', body: {} }, shopSyncResponseSchema),
    getProducts: () => request({ method: 'GET', path: '/shop/products' }, productsSchema),
    getCustomers: () => request({ method: 'GET', path: '/customers' }, customersSchema),
    getOrganization: () => request({ method: 'GET', path: '/team/organization' }, organizationSchema),
    getPersonActivity: (id, days) =>
      request(
        { method: 'GET', path: `/people/${segment(id)}/activity${days === undefined ? '' : `?days=${days}`}` },
        personSchema,
      ),
    getLog: (query) => {
      const params = new URLSearchParams();
      if (query.since !== undefined) params.set('since', String(query.since));
      if (query.limit !== undefined) params.set('limit', String(query.limit));
      const suffix = params.size > 0 ? `?${params.toString()}` : '';
      return request({ method: 'GET', path: `/log${suffix}` }, logSchema);
    },
    getLearning: () => request({ method: 'GET', path: '/learning' }, learningSchema),
    revertLearningChange: (input) =>
      request(
        {
          method: 'POST',
          path: `/learning/changes/${segment(input.changeId)}/revert`,
          body: { reason: input.reason ?? '', confirmedByHold: input.confirmedByHold },
        },
        revertResultSchema,
      ),
    decideLearningBatch: (input) =>
      request(
        {
          method: 'POST',
          path: `/learning/batches/${segment(input.batchId)}/decide`,
          body: {
            payloadHash: input.payloadHash,
            decisions: input.decisions,
            reason: input.reason,
            confirmedByHold: input.confirmedByHold,
          },
        },
        learningBatchResultSchema,
      ),
    async labelFit(input) {
      await request({ method: 'POST', path: '/learning/fit-labels', body: input }, fitLabelResponseSchema);
    },
  };
}
